import { ClientPurseItem } from './types'

export const removeDuplicateChildren = <T extends ClientPurseItem>(
  data: T[],
): T[] => {
  const childKeys = new Set<number>()

  data.forEach((item) => {
    if (item.children) {
      item.children.forEach((child) => {
        childKeys.add(child.key)
      })
    }
  })

  return data.filter((item) => !childKeys.has(item.key))
}

export const removeGroupElements = <T extends ClientPurseItem>(
  data: T[],
): T[] => {
  return data.filter((item) => item.clientPurseId !== null)
}

// Строки блока в группировку кошельков не участвуют
export const belongsToLocalMethod = (item: ClientPurseItem): boolean =>
  item.rowType === 'local_method' ||
  item.rowType === 'gateway' ||
  item.rowType === 'gateway_purse' ||
  item.rowType === 'local_method_total'

export const hasNoPurseDetails = (item: ClientPurseItem): boolean =>
  item.rowType === 'local_method' ||
  (item.rowType === 'gateway' && !item.clientPurseId)

// Local Method API rows are split by gateway. Recreate the purse groups so
// repeated gateways of the same purse are shown as one purse inside a group.
export const withLocalPurseGroups = (
  items: ClientPurseItem[],
): ClientPurseItem[] =>
  items.map((item) => {
    if (item.rowType !== 'local_method') return item

    const groups = new Map<number, ClientPurseItem>()
    const ungrouped: ClientPurseItem[] = []

    ;(item.children ?? []).forEach((child) => {
      if (!child.clientPurseId || !child.purseGroupId) {
        ungrouped.push(child)
        return
      }

      let group = groups.get(child.purseGroupId)
      if (!group) {
        group = {
          ...child,
          key: -(item.key * 1000000 + child.purseGroupId),
          rowType: 'group',
          gatewayId: null,
          clientPurseId: null,
          isTrusted: null,
          isMainInGroup: false,
          turnover: {
            currency: child.turnover.currency,
            deposit: 0,
            withdrawal: 0,
            total: 0,
            depositUSD: 0,
            withdrawalUSD: 0,
            totalUSD: 0,
          },
          children: [],
        }
        groups.set(child.purseGroupId, group)
      }

      const groupedPurse = group.children?.find(
        (candidate) => candidate.clientPurseId === child.clientPurseId,
      )

      if (groupedPurse) {
        groupedPurse.turnover.deposit += child.turnover.deposit
        groupedPurse.turnover.withdrawal += child.turnover.withdrawal
        groupedPurse.turnover.total =
          groupedPurse.turnover.deposit - groupedPurse.turnover.withdrawal
        groupedPurse.turnover.depositUSD += child.turnover.depositUSD
        groupedPurse.turnover.withdrawalUSD += child.turnover.withdrawalUSD
        groupedPurse.turnover.totalUSD += child.turnover.totalUSD

        if (child.isMainInGroup) {
          groupedPurse.isMainInGroup = true
          groupedPurse.bankName = child.bankName
          groupedPurse.mask = child.mask
          groupedPurse.clientName = child.clientName
          groupedPurse.ownerClassificationStatus =
            child.ownerClassificationStatus
        }
      } else {
        group.children?.push({
          ...child,
          rowType: 'purse',
          gatewayId: null,
        })
      }

      group.turnover.deposit += child.turnover.deposit
      group.turnover.withdrawal += child.turnover.withdrawal
      group.turnover.total = group.turnover.deposit - group.turnover.withdrawal
      group.turnover.depositUSD += child.turnover.depositUSD
      group.turnover.withdrawalUSD += child.turnover.withdrawalUSD
      group.turnover.totalUSD += child.turnover.totalUSD
    })

    groups.forEach((group) => {
      const representative =
        group.children?.find((child) => child.isMainInGroup) ??
        group.children?.[0]

      if (!representative) return

      group.bankName = representative.bankName
      group.mask = representative.mask
      group.clientName = representative.clientName
      group.ownerClassificationStatus = representative.ownerClassificationStatus
    })

    return {
      ...item,
      children: [...groups.values(), ...ungrouped],
    }
  })
